
/* =========================================================
   ForexWebSocket.js
   DERIV PUBLIC MARKET DATA WEBSOCKET
   ========================================================= */

   const DERIV_WS_URLS = [
    "wss://api.derivws.com/trading/v1/options/ws/public",
    "wss://ws.binaryws.com/websockets/v3",
  ];
  
  const CONNECTION_TIMEOUT = 10000;
  const INITIAL_RECONNECT_DELAY = 2000;
  const MAX_RECONNECT_DELAY = 10000;
  const ENDPOINT_SWITCH_DELAY = 1000;
  const PING_INTERVAL = 30000;
  
  const WS_OPEN = 1;
  const WS_CONNECTING = 0;
  
  class ForexWebSocket {
    constructor() {
      this.socket = null;
      this.connected = false;
      this.connecting = false;
      this.hasOpened = false;
  
      // Existing onTick(callback) listeners.
      this.listeners = new Set();
  
      // Optional event-specific listeners.
      this.eventListeners = new Map();
  
      this.queue = [];
      this.requests = new Map();
  
      // key -> subscription id
      this.subscriptions = new Map();
  
      // subscription id -> metadata
      this.subscriptionMeta = new Map();
  
      this.activeTickSymbols = new Set();
      this.activeCandleRequests = new Map();
      this.currentCandleKey = null;
  
      this.reqId = 0;
      this.urlIndex = 0;
      this.url = DERIV_WS_URLS[0];
  
      this.reconnectTimer = null;
      this.connectionTimeoutTimer = null;
      this.pingTimer = null;
  
      this.shouldReconnect = true;
      this.reconnectDelay = INITIAL_RECONNECT_DELAY;
  
      this.lastError = null;
      this.lastCloseCode = null;
      this.lastCloseReason = null;
    }
  
    /* ---------------- REQUEST ID ---------------- */
  
    nextReqId() {
      this.reqId += 1;
      return this.reqId;
    }
  
    getCurrentUrl() {
      return DERIV_WS_URLS[this.urlIndex];
    }
  
    isLegacyEndpoint() {
      return this.getCurrentUrl() ===
        "wss://ws.binaryws.com/websockets/v3";
    }
  
    getHistorySubscribeValue() {
      return this.isLegacyEndpoint() ? 1 : 0;
    }
  
    getConnectionInfo() {
      return {
        connected: this.connected,
        connecting: this.connecting,
        url: this.getCurrentUrl(),
        urlIndex: this.urlIndex,
        endpointCount: DERIV_WS_URLS.length,
        legacy: this.isLegacyEndpoint(),
        lastError: this.lastError,
        lastCloseCode: this.lastCloseCode,
        lastCloseReason: this.lastCloseReason,
      };
    }
  
    /* ---------------- EVENT LISTENERS ---------------- */
  
    onTick(callback) {
      if (typeof callback !== "function") {
        return () => {};
      }
  
      this.listeners.add(callback);
  
      // Return cleanup function for React useEffect.
      return () => {
        this.listeners.delete(callback);
      };
    }
  
    on(eventName, callback) {
      if (typeof callback !== "function") {
        return () => {};
      }
  
      if (!this.eventListeners.has(eventName)) {
        this.eventListeners.set(eventName, new Set());
      }
  
      this.eventListeners.get(eventName).add(callback);
  
      return () => {
        this.off(eventName, callback);
      };
    }
  
    off(eventName, callback) {
      this.eventListeners.get(eventName)?.delete(callback);
    }
  
    onData(callback) {
      return this.on("data", callback);
    }
  
    onConnection(callback) {
      return this.on("connection", callback);
    }
  
    onError(callback) {
      return this.on("error", callback);
    }
  
    emit(data) {
      // Preserve compatibility with existing onTick listeners.
      const callbacks = [...this.listeners];
  
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error("Deriv listener error:", error);
        }
      });
  
      // Dispatch typed events too.
      const eventName = data?.type || data?.msg_type;
  
      if (eventName) {
        this.eventListeners.get(eventName)?.forEach((callback) => {
          try {
            callback(data);
          } catch (error) {
            console.error(`Deriv ${eventName} listener error:`, error);
          }
        });
      }
  
      if (data?.error) {
        this.eventListeners.get("error")?.forEach((callback) => {
          try {
            callback(data.error);
          } catch (error) {
            console.error("Deriv error listener failed:", error);
          }
        });
      }
    }
  
    emitConnection(extra = {}) {
      this.emit({
        type: "connection",
        connected: this.connected,
        connecting: this.connecting,
        ...extra,
      });
    }
  
    /* ---------------- CONNECT ---------------- */
  
    connect() {
      if (
        this.socket &&
        (
          this.socket.readyState === WS_OPEN ||
          this.socket.readyState === WS_CONNECTING
        )
      ) {
        return;
      }
  
      if (typeof WebSocket === "undefined") {
        this.lastError = "WebSocket is not supported by this browser.";
        this.connecting = false;
        this.emitConnection({ error: this.lastError });
        return;
      }
  
      this.shouldReconnect = true;
  
      clearTimeout(this.reconnectTimer);
      clearTimeout(this.connectionTimeoutTimer);
  
      this.reconnectTimer = null;
      this.connectionTimeoutTimer = null;
  
      const url = this.getCurrentUrl();
      this.url = url;
  
      this.connecting = true;
      this.hasOpened = false;
      this.lastError = null;
  
      this.emitConnection({ url });
  
      console.log("Connecting to Deriv:", url);
  
      let socket;
  
      try {
        socket = new WebSocket(url);
        this.socket = socket;
      } catch (error) {
        this.lastError = error?.message || "Failed to create WebSocket.";
        this.connecting = false;
  
        this.emitConnection({ error: this.lastError, url });
        this.tryNextEndpoint();
        return;
      }
  
      this.connectionTimeoutTimer = setTimeout(() => {
        if (
          this.socket === socket &&
          socket.readyState === WS_CONNECTING
        ) {
          this.lastError = "Connection timeout.";
          console.error(this.lastError, url);
  
          try {
            socket.close();
          } catch (error) {
            console.error("Socket timeout close failed:", error);
            this.tryNextEndpoint();
          }
        }
      }, CONNECTION_TIMEOUT);
  
      socket.onopen = () => {
        if (this.socket !== socket) return;
  
        clearTimeout(this.connectionTimeoutTimer);
        this.connectionTimeoutTimer = null;
  
        this.connected = true;
        this.connecting = false;
        this.hasOpened = true;
        this.lastError = null;
        this.lastCloseCode = null;
        this.lastCloseReason = null;
        this.reconnectDelay = INITIAL_RECONNECT_DELAY;
  
        console.log("Deriv WebSocket connected:", url);
  
        this.startPing();
        this.flushQueue();
  
        this.emitConnection({ url });
  
        // Restore desired subscriptions after queued requests.
        this.activeSymbols();
        this.restoreSubscriptions();
      };
  
      socket.onmessage = (event) => {
        if (this.socket !== socket) return;
  
        let data;
  
        try {
          data = JSON.parse(event.data);
        } catch (error) {
          console.error("Invalid Deriv WebSocket JSON:", event.data);
          return;
        }
  
        if (data?.error) {
          this.lastError = data.error.message || "Deriv API error";
          console.error("Deriv API error:", data.error);
        }
  
        this.processResponse(data);
  
        // Main compatibility event:
        // onTick receives each raw response, including live tick messages.
        this.emit(data);
  
        // Also publish a typed tick event for consumers using on("tick").
        if (data?.msg_type === "tick" && data.tick) {
          this.emit({
            ...data,
            type: "tick",
          });
        }
  
        if (data?.msg_type === "active_symbols") {
          this.emit({
            type: "symbols",
            active_symbols: data.active_symbols || [],
            req_id: data.req_id,
          });
        }
      };
  
      socket.onerror = (error) => {
        if (this.socket !== socket) return;
  
        this.lastError = "WebSocket connection error.";
  
        console.error("Deriv WebSocket error:", url, error);
  
        this.emitConnection({
          error: this.lastError,
          url,
        });
  
        this.emit({
          type: "connection_error",
          error: this.lastError,
          url,
        });
      };
  
      socket.onclose = (event) => {
        // Ignore callbacks from an older socket.
        if (this.socket !== socket) return;
  
        clearTimeout(this.connectionTimeoutTimer);
        this.connectionTimeoutTimer = null;
  
        const opened = this.hasOpened;
  
        this.connected = false;
        this.connecting = false;
        this.hasOpened = false;
  
        this.stopPing();
  
        this.lastCloseCode = event.code;
        this.lastCloseReason = event.reason || "";
  
        this.socket = null;
  
        // Subscription IDs from the old connection are no longer valid.
        this.subscriptions.clear();
        this.subscriptionMeta.clear();
  
        console.warn("Deriv WebSocket closed:", {
          url,
          code: event.code,
          reason: event.reason,
        });
  
        this.emitConnection({
          code: event.code,
          reason: event.reason || "",
          url,
        });
  
        if (!this.shouldReconnect) return;
  
        if (!opened) {
          this.tryNextEndpoint();
        } else {
          this.scheduleReconnect();
        }
      };
    }
  
    /* ---------------- ENDPOINT FALLBACK ---------------- */
  
    tryNextEndpoint() {
      if (!this.shouldReconnect) return;
  
      clearTimeout(this.reconnectTimer);
  
      this.urlIndex = (this.urlIndex + 1) % DERIV_WS_URLS.length;
      this.url = this.getCurrentUrl();
  
      console.warn("Trying alternate Deriv endpoint:", this.url);
  
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, ENDPOINT_SWITCH_DELAY);
    }
  
    scheduleReconnect() {
      if (!this.shouldReconnect || this.reconnectTimer) return;
  
      const delay = this.reconnectDelay;
  
      console.log(`Reconnecting to Deriv in ${delay}ms`);
  
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, delay);
  
      this.reconnectDelay = Math.min(
        this.reconnectDelay * 1.5,
        MAX_RECONNECT_DELAY
      );
    }
  
    /* ---------------- SEND / QUEUE ---------------- */
  
    send(payload) {
      if (!payload) return null;
  
      if (
        this.socket &&
        this.socket.readyState === WS_OPEN
      ) {
        try {
          this.socket.send(JSON.stringify(payload));
          return payload.req_id ?? null;
        } catch (error) {
          console.error("Deriv send error:", error);
          this.queue.push(payload);
          return payload.req_id ?? null;
        }
      }
  
      this.queue.push(payload);
  
      if (
        !this.socket ||
        this.socket.readyState !== WS_CONNECTING
      ) {
        this.connect();
      }
  
      return payload.req_id ?? null;
    }
  
    flushQueue() {
      if (
        !this.socket ||
        this.socket.readyState !== WS_OPEN
      ) {
        return;
      }
  
      while (this.queue.length > 0) {
        const payload = this.queue.shift();
  
        try {
          this.socket.send(JSON.stringify(payload));
        } catch (error) {
          console.error("Deriv queue send error:", error);
          this.queue.unshift(payload);
          break;
        }
      }
    }
  
    registerRequest(reqId, metadata) {
      if (reqId == null) return;
      this.requests.set(Number(reqId), metadata);
    }
  
    /* ---------------- RESPONSE PROCESSING ---------------- */
  
    processResponse(data) {
      const reqId =
        data?.req_id != null ? Number(data.req_id) : null;
  
      const metadata =
        reqId != null ? this.requests.get(reqId) : null;
  
      const subscriptionId = data?.subscription?.id;
  
      if (data?.error && reqId != null) {
        this.requests.delete(reqId);
        return;
      }
  
      if (subscriptionId && metadata) {
        if (
          metadata.type === "ticks" ||
          metadata.type === "tick_history"
        ) {
          const symbol = metadata.symbol;
  
          if (symbol) {
            this.subscriptions.set(`ticks:${symbol}`, subscriptionId);
  
            this.subscriptionMeta.set(subscriptionId, {
              type: "ticks",
              symbol,
            });
          }
        }
  
        if (metadata.type === "candles") {
          const key = this.makeCandleKey(
            metadata.symbol,
            metadata.granularity
          );
  
          this.subscriptions.set(key, subscriptionId);
  
          this.subscriptionMeta.set(subscriptionId, {
            type: "candles",
            symbol: metadata.symbol,
            granularity: metadata.granularity,
          });
        }
      }
  
      // Keep request metadata for streaming subscriptions only.
      if (
        reqId != null &&
        metadata &&
        !data.subscription
      ) {
        this.requests.delete(reqId);
      }
  
      if (data?.msg_type === "tick" && data.tick?.symbol) {
        this.activeTickSymbols.add(data.tick.symbol);
      }
    }
  
    /* ---------------- PING ---------------- */
  
    startPing() {
      this.stopPing();
  
      this.pingTimer = setInterval(() => {
        if (
          this.socket &&
          this.socket.readyState === WS_OPEN
        ) {
          this.send({ ping: 1 });
        }
      }, PING_INTERVAL);
    }
  
    stopPing() {
      if (this.pingTimer) {
        clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
    }
  
    /* ---------------- ACTIVE SYMBOLS ---------------- */
  
    activeSymbols() {
      const reqId = this.nextReqId();
  
      this.registerRequest(reqId, {
        type: "active_symbols",
      });
  
      this.send({
        active_symbols: "full",
        req_id: reqId,
      });
  
      return reqId;
    }
  
    /* ---------------- LIVE TICKS ---------------- */
  
    ticks(symbol) {
      if (!symbol) return null;
  
      this.activeTickSymbols.add(symbol);
  
      const existing = this.subscriptions.get(`ticks:${symbol}`);
  
      if (existing && this.connected) {
        return existing;
      }
  
      // Avoid sending the same subscription repeatedly while awaiting response.
      const pending = [...this.requests.values()].some(
        (request) =>
          request.type === "ticks" &&
          request.symbol === symbol
      );
  
      if (pending && this.connected) {
        return null;
      }
  
      const reqId = this.nextReqId();
  
      this.registerRequest(reqId, {
        type: "ticks",
        symbol,
      });
  
      this.send({
        ticks: symbol,
        subscribe: 1,
        req_id: reqId,
      });
  
      return reqId;
    }
  
    forgetTick(symbol) {
      if (!symbol) return;
  
      this.activeTickSymbols.delete(symbol);
  
      const key = `ticks:${symbol}`;
      const subscriptionId = this.subscriptions.get(key);
  
      if (subscriptionId) {
        this.forget(subscriptionId);
        this.subscriptions.delete(key);
        this.subscriptionMeta.delete(subscriptionId);
      }
    }
  
    /* ---------------- TICK HISTORY ---------------- */
  
    history(symbol, count = 70) {
      if (!symbol) return null;
  
      const reqId = this.nextReqId();
  
      this.registerRequest(reqId, {
        type: "tick_history",
        symbol,
        count,
      });
  
      this.send({
        ticks_history: symbol,
        count,
        end: "latest",
        style: "ticks",
        subscribe: this.getHistorySubscribeValue(),
        req_id: reqId,
      });
  
      return reqId;
    }
  
    subscribeHistory(symbol, count = 70) {
      if (!symbol) return null;
  
      this.activeTickSymbols.add(symbol);
  
      const reqId = this.nextReqId();
  
      this.registerRequest(reqId, {
        type: "tick_history",
        symbol,
        count,
      });
  
      this.send({
        ticks_history: symbol,
        count,
        end: "latest",
        style: "ticks",
        subscribe: 1,
        req_id: reqId,
      });
  
      return reqId;
    }
  
    /* ---------------- CANDLES ---------------- */
  
    makeCandleKey(symbol, granularity) {
      return `candles:${symbol}:${granularity}`;
    }
  
    candles(symbol, granularity = 300, count = 150) {
      if (!symbol) return null;
  
      const seconds = Number(granularity);
  
      if (!Number.isFinite(seconds) || seconds <= 0) {
        console.error("Invalid candle granularity:", granularity);
        return null;
      }
  
      const key = this.makeCandleKey(symbol, seconds);
  
      if (this.currentCandleKey === key) {
        return null;
      }
  
      if (this.currentCandleKey) {
        const oldId = this.subscriptions.get(this.currentCandleKey);
  
        if (oldId) {
          this.forget(oldId);
          this.subscriptions.delete(this.currentCandleKey);
          this.subscriptionMeta.delete(oldId);
        }
      }
  
      this.currentCandleKey = key;
  
      this.activeCandleRequests.set(key, {
        symbol,
        granularity: seconds,
        count,
      });
  
      const reqId = this.nextReqId();
  
      this.registerRequest(reqId, {
        type: "candles",
        symbol,
        granularity: seconds,
        count,
      });
  
      this.send({
        ticks_history: symbol,
        adjust_start_time: 1,
        count,
        end: "latest",
        style: "candles",
        granularity: seconds,
        subscribe: 1,
        req_id: reqId,
      });
  
      return reqId;
    }
  
    /* ---------------- RESTORE SUBSCRIPTIONS ---------------- */
  
    restoreSubscriptions() {
      // Socket is already OPEN when this is called.
      this.activeTickSymbols.forEach((symbol) => {
        const reqId = this.nextReqId();
  
        this.registerRequest(reqId, {
          type: "ticks",
          symbol,
        });
  
        this.send({
          ticks: symbol,
          subscribe: 1,
          req_id: reqId,
        });
      });
  
      this.activeCandleRequests.forEach((request) => {
        const reqId = this.nextReqId();
  
        this.registerRequest(reqId, {
          type: "candles",
          symbol: request.symbol,
          granularity: request.granularity,
          count: request.count,
        });
  
        this.send({
          ticks_history: request.symbol,
          adjust_start_time: 1,
          count: request.count,
          end: "latest",
          style: "candles",
          granularity: request.granularity,
          subscribe: 1,
          req_id: reqId,
        });
      });
    }
  
    /* ---------------- FORGET ---------------- */
  
    forget(subscriptionId) {
      if (!subscriptionId) return null;
  
      const reqId = this.nextReqId();
  
      this.registerRequest(reqId, {
        type: "forget",
        subscriptionId,
      });
  
      this.send({
        forget: subscriptionId,
        req_id: reqId,
      });
  
      return reqId;
    }
  
    forgetAll() {
      const reqId = this.nextReqId();
  
      this.registerRequest(reqId, {
        type: "forget_all",
      });
  
      this.send({
        forget_all: "ticks",
        req_id: reqId,
      });
  
      this.subscriptions.clear();
      this.subscriptionMeta.clear();
      this.activeTickSymbols.clear();
      this.activeCandleRequests.clear();
      this.currentCandleKey = null;
  
      return reqId;
    }
  
    /* ---------------- CONNECTION STATUS ---------------- */
  
    isConnected() {
      return Boolean(
        this.socket &&
        this.socket.readyState === WS_OPEN
      );
    }
  
    isConnecting() {
      return Boolean(
        this.socket &&
        this.socket.readyState === WS_CONNECTING
      );
    }
  
    getReadyState() {
      return this.socket ? this.socket.readyState : null;
    }
  
    getActiveTickSymbols() {
      return Array.from(this.activeTickSymbols);
    }
  
    getSubscriptions() {
      return Array.from(this.subscriptions.entries());
    }
  
    getRequest(reqId) {
      if (reqId == null) return null;
      return this.requests.get(Number(reqId)) || null;
    }
  
    /* ---------------- CLOSE / DESTROY ---------------- */
  
    close() {
      this.shouldReconnect = false;
  
      clearTimeout(this.reconnectTimer);
      clearTimeout(this.connectionTimeoutTimer);
  
      this.reconnectTimer = null;
      this.connectionTimeoutTimer = null;
  
      this.stopPing();
  
      this.connected = false;
      this.connecting = false;
      this.hasOpened = false;
  
      this.queue = [];
      this.requests.clear();
      this.subscriptions.clear();
      this.subscriptionMeta.clear();
  
      this.activeTickSymbols.clear();
      this.activeCandleRequests.clear();
      this.currentCandleKey = null;
  
      const oldSocket = this.socket;
      this.socket = null;
  
      if (oldSocket) {
        try {
          oldSocket.onopen = null;
          oldSocket.onmessage = null;
          oldSocket.onerror = null;
          oldSocket.onclose = null;
          oldSocket.close();
        } catch (error) {
          console.error("Deriv close error:", error);
        }
      }
  
      this.emitConnection();
      console.log("Deriv WebSocket closed.");
    }
  
    removeAllListeners() {
      this.listeners.clear();
      this.eventListeners.clear();
    }
  
    destroy() {
      this.close();
      this.removeAllListeners();
    }
  }
  
  /* ---------------- SINGLETON EXPORT ---------------- */
  
  const websocket = new ForexWebSocket();
  
  export default websocket;
  export { ForexWebSocket };
  















